import React from 'react';
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import ProfileCompletionWrapper from "@/components/ProfileCompletionWrapper";
import Header from "@/components/layout/Header";
import AutoLogoutWrapper from "./components/AutoLogoutWrapper";
import ErrorBoundary from "@/components/ErrorBoundary";

// Code splitting: lazy load all page components
const Index = React.lazy(() => import("./pages/Index"));
const Quiz = React.lazy(() => import("./pages/Quiz"));
const Results = React.lazy(() => import("./pages/Results"));
const Auth = React.lazy(() => import("./pages/Auth"));
const Dashboard = React.lazy(() => import("./pages/Dashboard"));
const History = React.lazy(() => import("./pages/History"));
const Exams = React.lazy(() => import("./pages/Exams"));
const Learning = React.lazy(() => import("./pages/Learning"));
const AdminLearning = React.lazy(() => import("./pages/AdminLearning"));
const Gamification = React.lazy(() => import("./pages/Gamification"));
const Profile = React.lazy(() => import("./pages/Profile"));
const AdminLayout = React.lazy(() => import("./pages/admin/AdminLayout"));
const AdminDashboard = React.lazy(() => import("./pages/admin/AdminDashboard"));
const AdminUsers = React.lazy(() => import("./pages/admin/AdminUsers"));
const AdminDataCleanup = React.lazy(() => import("./pages/admin/AdminDataCleanup"));
const AdminAIQuestions = React.lazy(() => import("./pages/admin/AdminAIQuestions"));
const AdminQuestions = React.lazy(() => import("./pages/admin/AdminQuestions"));
const AdminExams = React.lazy(() => import("./pages/admin/AdminExams"));
const AdminAudit = React.lazy(() => import("./pages/admin/AdminAudit"));
const AdminAILogs = React.lazy(() => import("./pages/admin/AdminAILogs"));
const AdminCertifications = React.lazy(() => import("./pages/admin/AdminCertifications"));
const AdminVendors = React.lazy(() => import("./pages/admin/AdminVendors"));
const AdminUserPermissions = React.lazy(() => import("./pages/admin/AdminUserPermissions"));
const AdaptiveEngineTest = React.lazy(() => import("./pages/admin/AdaptiveEngineTest"));
const VendorCertifications = React.lazy(() => import("./pages/VendorCertifications"));
const CertificationExams = React.lazy(() => import("./pages/CertificationExams"));
const VendorPage = React.lazy(() => import("./pages/VendorPage"));
const NotFound = React.lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient();

const App = () => {
  return (
    <ThemeProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <LanguageProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <BrowserRouter>
                <AutoLogoutWrapper>
                  <ProfileCompletionWrapper>
                    <div className="min-h-screen bg-background">
                      <Header />
                      <ErrorBoundary>
                        <React.Suspense fallback={<div className="flex items-center justify-center min-h-screen"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>}>
                          <Routes>
                            <Route path="/" element={<Index />} />
                            <Route path="/auth" element={<Auth />} />
                            <Route path="/dashboard" element={<Dashboard />} />
                            <Route path="/exams" element={<Exams />} />
                            <Route path="/quiz/:examId" element={<Quiz />} />
                            <Route path="/results" element={<Results />} />
                            <Route path="/history" element={<History />} />
                            <Route path="/learning" element={<Learning />} />
                            <Route path="/admin/learning" element={<AdminLearning />} />
                            <Route path="/gamification" element={<Gamification />} />
                            <Route path="/profile" element={<Profile />} />

                            {/* Vendor Routes */}
                            <Route path="/vendors/:slug" element={<VendorPage />} />
                            <Route path="/vendors/:vendorSlug/certifications" element={<VendorCertifications />} />
                            <Route path="/vendors/:vendorSlug/certifications/:certificationSlug" element={<CertificationExams />} />

                            {/* Admin Routes */}
                            <Route path="/admin" element={<AdminLayout />}>
                              <Route index element={<AdminDashboard />} />
                              <Route path="users" element={<AdminUsers />} />
                              <Route path="permissions" element={<AdminUserPermissions />} />
                              <Route path="cleanup" element={<AdminDataCleanup />} />
                              <Route path="certifications" element={<AdminCertifications />} />
                              <Route path="vendors" element={<AdminVendors />} />
                              <Route path="ai-questions" element={<AdminAIQuestions />} />
                              <Route path="questions" element={<AdminQuestions />} />
                              <Route path="exams" element={<AdminExams />} />
                              <Route path="audit" element={<AdminAudit />} />
                              <Route path="ai-logs" element={<AdminAILogs />} />
                              <Route path="adaptive-test" element={<AdaptiveEngineTest />} />
                            </Route>

                            <Route path="*" element={<NotFound />} />
                          </Routes>
                        </React.Suspense>
                      </ErrorBoundary>
                    </div>
                  </ProfileCompletionWrapper>
                </AutoLogoutWrapper>
              </BrowserRouter>
            </TooltipProvider>
          </LanguageProvider>
        </AuthProvider>
      </QueryClientProvider>
    </ThemeProvider>
  );
};

export default App;
